import React, { useState, useEffect } from 'react';
import { Plus, Copy, Key } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Button, Badge, Modal, Input, Tabs,
  Table, THead, TBody, TR, TH, TD, LoadingState, EmptyState,
} from '../../components/ui';

const TABS = [
  { key: 'keys', label: 'API Keys' },
  { key: 'logs', label: 'Student Logs' },
];

const ThirdPartyManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', source: '', qualifyingMarks: 0 });
  const [activeTab, setActiveTab] = useState('keys');
  const [editMode, setEditMode] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState(null);

  const fetchKeys = async () => {
    try {
      const res = await api.get('/admin/api-keys');
      setApiKeys(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load API keys');
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.get('/admin/api-keys/logs');
      setLogs(res.data.data?.students || []);
    } catch (err) {
      toast.error('Failed to load logs');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchKeys(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/admin/api-keys/${selectedKeyId}`, formData);
        toast.success('API Key updated');
      } else {
        await api.post('/admin/api-keys', formData);
        toast.success('API Key generated');
      }
      setShowModal(false);
      setFormData({ name: '', source: '', qualifyingMarks: 0 });
      setEditMode(false);
      setSelectedKeyId(null);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process request');
    }
  };

  const handleEditClick = (key) => {
    setFormData({ name: key.name, source: key.source, qualifyingMarks: key.qualifyingMarks || 0 });
    setSelectedKeyId(key._id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleToggleStatus = async (id) => {
    try {
      await api.patch(`/admin/api-keys/${id}/toggle`);
      toast.success('Status updated');
      fetchKeys();
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">3rd Party Integration</h2>
          <p className="text-slate-500 text-sm mt-1">Manage API access for external partners</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setFormData({ name: '', source: '', qualifyingMarks: 0 });
            setEditMode(false);
            setShowModal(true);
          }}
        >
          Generate API Key
        </Button>
      </div>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {loading ? (
        <LoadingState label="Loading..." />
      ) : activeTab === 'keys' ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {apiKeys.length === 0 ? (
            <EmptyState icon={Key} title="No API keys found" description="Generate one to let a partner register students." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Partner Name</TH>
                  <TH>Source ID</TH>
                  <TH>API Key</TH>
                  <TH>Qualifying Marks</TH>
                  <TH>Status</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {apiKeys.map((key) => (
                  <TR key={key._id}>
                    <TD className="font-medium text-slate-900">{key.name}</TD>
                    <TD>{key.source}</TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                          {key.key.substring(0, 10)}...{key.key.substring(key.key.length - 4)}
                        </code>
                        <button onClick={() => copyToClipboard(key.key)} className="text-slate-400 hover:text-themePrimary">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </TD>
                    <TD className="font-medium">{key.qualifyingMarks || 0}</TD>
                    <TD><Badge variant={key.isActive ? 'success' : 'danger'}>{key.isActive ? 'Active' : 'Inactive'}</Badge></TD>
                    <TD>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(key._id)}>
                          {key.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEditClick(key)}>Edit</Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {logs.length === 0 ? (
            <EmptyState title="No student logs found" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Student Name</TH>
                  <TH>Email</TH>
                  <TH>Source Partner</TH>
                  <TH>Registration Date</TH>
                </TR>
              </THead>
              <TBody>
                {logs.map((student) => (
                  <TR key={student._id}>
                    <TD className="font-medium text-slate-900">{student.name}</TD>
                    <TD>{student.email}</TD>
                    <TD><Badge variant="brand">{student.apiKey?.name || student.source}</Badge></TD>
                    <TD>{new Date(student.createdAt).toLocaleString()}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => { setShowModal(false); setEditMode(false); }}
        title={editMode ? 'Edit API Key Details' : 'Generate New API Key'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => { setShowModal(false); setEditMode(false); }}>Cancel</Button>
            <Button variant="primary" type="submit" form="apikey-form">{editMode ? 'Update Details' : 'Generate'}</Button>
          </>
        }
      >
        <form id="apikey-form" onSubmit={handleCreateKey} className="space-y-4">
          <Input
            label="Partner Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. EdTech Solutions"
          />
          <Input
            label="Source ID (Identifier)"
            required
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
            placeholder="e.g. edtech_prod"
          />
          <Input
            label="Qualifying Marks"
            type="number"
            min="0"
            required
            value={formData.qualifyingMarks}
            onChange={(e) => setFormData({ ...formData, qualifyingMarks: e.target.value })}
            placeholder="e.g. 40"
            hint="Students scoring below this will be marked 'disqualified' in results."
          />
        </form>
      </Modal>
    </div>
  );
};

export default ThirdPartyManagement;
